import { createApi } from '@reduxjs/toolkit/query/react';

import { opsQuery } from 'store/query';
import { opsApiInstance } from 'api/instance';

import {
  AccountingOverviewProfit,
  AccountingOverviewSeries,
  AccountingOverviewSummary,
  AccountingOverviewRangeArgs,
  AccountingOverviewSeriesArgs,
  AccountingOverviewPayrollCard,
} from './types';

export * from './types';

// ACCOUNTING OVERVIEW (plan §4.1): read-only Ops routes under
// mailer/accounting/overview (CloudFront sends mailer/* to Ops). Nothing is
// read from Main and nothing per employee (plan §4.4). Its own Ops API
// slice (reducerPath accountingOverviewApi), registered in store.ts; it
// depends on no other slice (ORCESTRATOR 27 Oct: payroll's data layer is
// being rebuilt).

/** `path?a=1&b=2`, keeping 0 and dropping only undefined / null / ''. */
export const withParams = (
  path: string,
  params: Record<string, string | number | null | undefined>
) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });
  const text = search.toString();
  return text ? `${path}?${text}` : path;
};

const BASE = 'mailer/accounting/overview';

export const ACCOUNTING_OVERVIEW_URL = Object.freeze({
  SERIES: `${BASE}/series`,
  PROFIT: `${BASE}/profit`,
  SUMMARY: `${BASE}/summary`,
  EXPORT_CSV: `${BASE}/export.csv`,
  PAYROLL_CARD: `${BASE}/payroll-card`,
});

const TAG = 'AccountingOverview' as const;

// Never send the page to Not Found if Ops lacks a route (an older Ops build
// during a deploy): the card shows its own error instead (plan §6).
const headers = { 'allow-not-found': 'true' };

export const accountingOverviewApi = createApi({
  tagTypes: [TAG],
  baseQuery: opsQuery,
  reducerPath: 'accountingOverviewApi',
  endpoints: (build) => ({
    accountingOverviewPayrollCard: build.query<
      AccountingOverviewPayrollCard,
      void
    >({
      providesTags: [TAG],
      query: () => ({
        headers,
        method: 'get',
        url: ACCOUNTING_OVERVIEW_URL.PAYROLL_CARD,
      }),
    }),
    accountingOverviewProfit: build.query<
      AccountingOverviewProfit,
      AccountingOverviewRangeArgs
    >({
      providesTags: [TAG],
      query: ({ to, from }) => ({
        headers,
        method: 'get',
        url: withParams(ACCOUNTING_OVERVIEW_URL.PROFIT, { to, from }),
      }),
    }),
    accountingOverviewSummary: build.query<
      AccountingOverviewSummary,
      AccountingOverviewRangeArgs
    >({
      providesTags: [TAG],
      query: ({ to, from }) => ({
        headers,
        method: 'get',
        url: withParams(ACCOUNTING_OVERVIEW_URL.SUMMARY, { to, from }),
      }),
    }),
    accountingOverviewSeries: build.query<
      AccountingOverviewSeries,
      AccountingOverviewSeriesArgs
    >({
      providesTags: [TAG],
      query: ({ end, granularity }) => ({
        headers,
        method: 'get',
        url: withParams(ACCOUNTING_OVERVIEW_URL.SERIES, {
          end,
          granularity,
        }),
      }),
    }),
  }),
});

export const {
  useAccountingOverviewSeriesQuery,
  useAccountingOverviewProfitQuery,
  useAccountingOverviewSummaryQuery,
  useAccountingOverviewPayrollCardQuery,
} = accountingOverviewApi;

/** The page as CSV, built on Ops (owner Q10; plan §3.1 #3). */
export const fetchAccountingOverviewCsv = async ({
  to,
  from,
}: AccountingOverviewRangeArgs) => {
  const response = await opsApiInstance.get<Blob>(
    withParams(ACCOUNTING_OVERVIEW_URL.EXPORT_CSV, { to, from }),
    { headers, responseType: 'blob' }
  );
  return response.data;
};
